import React from "react";
import {
  ActivityIndicator,
  Image,
  Platform,
  Pressable,
  Text,
  View,
} from "react-native";

import { GalleryModal, SecondaryButton } from "../../../common/native";
import {
  getClinicGallery,
  type TClinicGalleryItem,
} from "../../../../services/api-clinic";
import { APP_TOKENS as tokens } from "../../../../styles/tokens";
import type { TSession } from "../../../../utils/types";
import { styles } from "./ImageToolsPage.styles";
import {
  getExportHistoryGridImageUrl,
  mergeUniqueExportHistoryItems,
} from "./exportHistoryUtils";
import { IMAGE_TOOLS_TOKENS as imageToolsTokens } from "./tokens";

const EXPORT_HISTORY_PAGE_LIMIT = 24;
const IMAGE_TOOL_EXPORT_UPLOAD_TYPE = "ImageToolExportImage";

type TExportHistoryState = {
  items: TClinicGalleryItem[];
  nextCursor: string | null;
  phase: "loading" | "ready" | "error";
  isRefreshing: boolean;
  isLoadingMore: boolean;
  error: {
    message: string;
    retryCursor: string | null;
  } | null;
};

const initialState: TExportHistoryState = {
  items: [],
  nextCursor: null,
  phase: "loading",
  isRefreshing: false,
  isLoadingMore: false,
  error: null,
};

const getHistoryErrorMessage = () =>
  "Export history could not be loaded. Check your connection and retry.";

const useImageToolsExportHistory = (
  session: TSession | null | undefined,
  refreshKey: number,
) => {
  const [state, setState] = React.useState(initialState);
  const activeControllerRef = React.useRef<AbortController | null>(null);
  const loadedClinicIdRef = React.useRef<string | null>(null);
  const hasLoadedRef = React.useRef(false);

  const loadFirstPage = React.useCallback(
    async (preserveItems: boolean) => {
      activeControllerRef.current?.abort();

      if (!session?.clinicId) {
        setState({
          ...initialState,
          phase: "error",
          error: {
            message:
              "Your clinic could not be identified. Sign in again, then retry.",
            retryCursor: null,
          },
        });
        return;
      }

      const controller = new AbortController();
      activeControllerRef.current = controller;
      setState((current) => ({
        ...current,
        items: preserveItems ? current.items : [],
        nextCursor: preserveItems ? current.nextCursor : null,
        phase: preserveItems && current.items.length > 0 ? "ready" : "loading",
        isRefreshing: preserveItems && current.items.length > 0,
        isLoadingMore: false,
        error: null,
      }));

      try {
        const response = await getClinicGallery(session, {
          mode: "chronological",
          limit: EXPORT_HISTORY_PAGE_LIMIT,
          uploadTypes: [IMAGE_TOOL_EXPORT_UPLOAD_TYPE],
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;

        hasLoadedRef.current = true;
        setState({
          items: mergeUniqueExportHistoryItems([], response.items || []),
          nextCursor: response.next_cursor || null,
          phase: "ready",
          isRefreshing: false,
          isLoadingMore: false,
          error: null,
        });
      } catch {
        if (controller.signal.aborted) return;
        setState((current) => ({
          ...current,
          phase: current.items.length > 0 ? "ready" : "error",
          isRefreshing: false,
          isLoadingMore: false,
          error: {
            message: getHistoryErrorMessage(),
            retryCursor: null,
          },
        }));
      } finally {
        if (activeControllerRef.current === controller) {
          activeControllerRef.current = null;
        }
      }
    },
    [session],
  );

  const loadMore = React.useCallback(
    async (cursor: string) => {
      if (!session?.clinicId || !cursor) return;

      activeControllerRef.current?.abort();
      const controller = new AbortController();
      activeControllerRef.current = controller;
      setState((current) => ({
        ...current,
        isLoadingMore: true,
        error: null,
      }));

      try {
        const response = await getClinicGallery(session, {
          mode: "chronological",
          limit: EXPORT_HISTORY_PAGE_LIMIT,
          cursor,
          uploadTypes: [IMAGE_TOOL_EXPORT_UPLOAD_TYPE],
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;

        setState((current) => ({
          ...current,
          items: mergeUniqueExportHistoryItems(
            current.items,
            response.items || [],
          ),
          nextCursor: response.next_cursor || null,
          phase: "ready",
          isLoadingMore: false,
          error: null,
        }));
      } catch {
        if (controller.signal.aborted) return;
        setState((current) => ({
          ...current,
          isLoadingMore: false,
          error: {
            message: getHistoryErrorMessage(),
            retryCursor: cursor,
          },
        }));
      } finally {
        if (activeControllerRef.current === controller) {
          activeControllerRef.current = null;
        }
      }
    },
    [session],
  );

  React.useEffect(() => {
    const clinicId = session?.clinicId || null;
    const isSameLoadedClinic =
      hasLoadedRef.current && loadedClinicIdRef.current === clinicId;

    if (loadedClinicIdRef.current !== clinicId) {
      hasLoadedRef.current = false;
      loadedClinicIdRef.current = clinicId;
    }

    void loadFirstPage(isSameLoadedClinic);

    return () => {
      activeControllerRef.current?.abort();
    };
  }, [loadFirstPage, refreshKey, session?.clinicId]);

  const retry = React.useCallback(() => {
    if (state.error?.retryCursor) {
      void loadMore(state.error.retryCursor);
      return;
    }
    void loadFirstPage(state.items.length > 0);
  }, [loadFirstPage, loadMore, state.error?.retryCursor, state.items.length]);

  return {
    ...state,
    retry,
    loadMore: () => {
      if (state.nextCursor && !state.isLoadingMore) {
        void loadMore(state.nextCursor);
      }
    },
  };
};

const formatExportDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const ExportHistoryCard: React.FC<{
  item: TClinicGalleryItem;
  itemNumber: number;
  cardStyle: object;
  onOpen: () => void;
}> = ({ item, itemNumber, cardStyle, onOpen }) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const [thumbnailFailed, setThumbnailFailed] = React.useState(false);
  const [fullSizeFailed, setFullSizeFailed] = React.useState(false);
  const imageUrl = getExportHistoryGridImageUrl(item, thumbnailFailed);
  const imageUnavailable = !imageUrl || fullSizeFailed;
  const exportDate = formatExportDate(item.display_date || item.created_at);
  const accessibilityLabel = exportDate
    ? `Open high-quality export ${itemNumber}, created ${exportDate}`
    : `Open high-quality export ${itemNumber}`;

  React.useEffect(() => {
    setThumbnailFailed(false);
    setFullSizeFailed(false);
  }, [item.full_size_url, item.thumbnail_url]);

  const handleImageError = () => {
    if (!thumbnailFailed && imageUrl === item.thumbnail_url) {
      setThumbnailFailed(true);
      return;
    }
    setFullSizeFailed(true);
  };

  const handleKeyDown = (event: {
    key?: string;
    nativeEvent?: { key?: string };
    preventDefault?: () => void;
  }) => {
    const key = event.key || event.nativeEvent?.key;
    if (key !== "Enter" && key !== " ") return;
    event.preventDefault?.();
    onOpen();
  };

  return (
    <Pressable
      testID={`image-tools-export-history-item-${item.item_key}`}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ disabled: imageUnavailable }}
      disabled={imageUnavailable}
      onPress={onOpen}
      {...(Platform.OS === "web" ? ({ onKeyDown: handleKeyDown } as any) : {})}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      style={({ pressed }) => [
        styles.exportHistoryCard,
        cardStyle,
        isHovered && !imageUnavailable && styles.exportHistoryCardHovered,
        isFocused && styles.focused,
        pressed && !imageUnavailable && styles.exportHistoryCardPressed,
        imageUnavailable && styles.disabled,
      ]}
    >
      {imageUnavailable ? (
        <View style={styles.exportHistoryUnavailable}>
          <Text style={styles.exportHistoryUnavailableText}>
            Preview unavailable
          </Text>
        </View>
      ) : (
        <>
          {Platform.OS === "web" ? (
            <img
              src={imageUrl}
              alt=""
              loading="lazy"
              decoding="async"
              onError={handleImageError}
              style={
                {
                  ...styles.exportHistoryImage,
                  objectFit: "cover",
                } as React.CSSProperties
              }
            />
          ) : (
            <Image
              source={{ uri: imageUrl }}
              resizeMode="cover"
              style={styles.exportHistoryImage}
              accessible={false}
              onError={handleImageError}
            />
          )}
        </>
      )}
      <View style={styles.exportHistoryCardFooter}>
        <Text numberOfLines={1} style={styles.exportHistoryCardTitle}>
          High-quality export
        </Text>
        {exportDate ? (
          <Text style={styles.exportHistoryCardDate}>{exportDate}</Text>
        ) : null}
      </View>
    </Pressable>
  );
};

export const ImageToolsExportHistory: React.FC<{
  session: TSession | null | undefined;
  refreshKey: number;
  viewportWidth: number;
}> = ({ session, refreshKey, viewportWidth }) => {
  const history = useImageToolsExportHistory(session, refreshKey);
  const [selectedItemKey, setSelectedItemKey] = React.useState<string | null>(
    null,
  );
  const isMobile = viewportWidth < imageToolsTokens.size.mobileBreakpoint;
  const isTablet =
    !isMobile && viewportWidth < imageToolsTokens.size.tabletBreakpoint;
  const cardStyle = isMobile
    ? styles.exportHistoryCardMobile
    : isTablet
      ? styles.exportHistoryCardTablet
      : styles.exportHistoryCardDesktop;
  const skeletonCount = isMobile ? 2 : isTablet ? 3 : 4;
  const previewItems = React.useMemo(
    () =>
      history.items.filter((item) => item.full_size_url || item.thumbnail_url),
    [history.items],
  );
  const selectedIndex = Math.max(
    previewItems.findIndex((item) => item.item_key === selectedItemKey),
    0,
  );

  React.useEffect(() => {
    if (
      selectedItemKey &&
      !previewItems.some((item) => item.item_key === selectedItemKey)
    ) {
      setSelectedItemKey(null);
    }
  }, [previewItems, selectedItemKey]);

  return (
    <View
      testID="image-tools-export-history"
      style={styles.exportHistoryCardSection}
    >
      <View style={styles.exportHistoryHeader}>
        <View style={styles.sectionHeaderCopy}>
          <Text accessibilityRole="header" style={styles.sectionTitle}>
            Export history
          </Text>
          <Text style={styles.sectionDescription}>
            Review high-quality Image Tools exports saved by your clinic. Newest
            exports appear first.
          </Text>
        </View>
        {history.isRefreshing ? (
          <View
            accessibilityLiveRegion="polite"
            style={styles.exportHistoryRefreshStatus}
          >
            <ActivityIndicator size="small" color={tokens.color.accent} />
            <Text style={styles.exportHistoryStatusText}>Refreshing…</Text>
          </View>
        ) : null}
      </View>

      {history.phase === "loading" ? (
        <View
          accessibilityLiveRegion="polite"
          style={styles.exportHistoryLoading}
        >
          <View style={styles.exportHistoryLoadingCopy}>
            <ActivityIndicator size="small" color={tokens.color.accent} />
            <Text style={styles.exportHistoryStatusText}>
              Loading export history…
            </Text>
          </View>
          <View style={styles.exportHistoryGrid}>
            {Array.from({ length: skeletonCount }, (_, index) => (
              <View
                key={index}
                style={[styles.exportHistorySkeleton, cardStyle]}
              />
            ))}
          </View>
        </View>
      ) : null}

      {history.error ? (
        <View style={styles.errorBanner}>
          <Text
            accessibilityLiveRegion="assertive"
            accessibilityRole="alert"
            style={styles.errorText}
          >
            {history.error.message}
          </Text>
          <SecondaryButton
            text="Retry"
            onPress={history.retry}
            disabled={history.isLoadingMore || history.isRefreshing}
            style={styles.retryButton}
          />
        </View>
      ) : null}

      {history.phase === "ready" && history.items.length === 0 ? (
        <View
          testID="image-tools-export-history-empty"
          style={styles.exportHistoryEmpty}
        >
          <Text style={styles.exportHistoryEmptyTitle}>No exports yet</Text>
          <Text style={styles.exportHistoryEmptyText}>
            Create a high-quality export from a processed result above and it
            will appear here.
          </Text>
        </View>
      ) : null}

      {history.items.length > 0 ? (
        <View style={styles.exportHistoryGrid}>
          {history.items.map((item, index) => (
            <ExportHistoryCard
              key={item.item_key}
              item={item}
              itemNumber={index + 1}
              cardStyle={cardStyle}
              onOpen={() => setSelectedItemKey(item.item_key)}
            />
          ))}
        </View>
      ) : null}

      {history.nextCursor && history.items.length > 0 && !history.error ? (
        <View style={styles.exportHistoryActions}>
          <SecondaryButton
            text="Load More"
            accessibilityLabel="Load more high-quality exports"
            onPress={history.loadMore}
            loading={history.isLoadingMore}
            disabled={history.isLoadingMore}
            fullWidth={isMobile}
            style={styles.compactButton}
          />
        </View>
      ) : null}

      <GalleryModal
        visible={selectedItemKey !== null}
        imageUrls={previewItems.map(
          (item) => item.full_size_url || item.thumbnail_url || "",
        )}
        thumbnailUrls={previewItems.map(
          (item) => item.thumbnail_url || item.full_size_url || "",
        )}
        selectedIndex={selectedIndex}
        onIndexChange={(index) =>
          setSelectedItemKey(previewItems[index]?.item_key || null)
        }
        onClose={() => setSelectedItemKey(null)}
        title="Export preview"
      />
    </View>
  );
};
